/**
 * GaugeControl
 * Displays numeric value as a gauge with min/max range
 * Supports 4 gauge variants: circular, linear, thermometer, progress-ring
 * Performance: Memoized to prevent unnecessary re-renders
 */

import React, { useMemo, memo } from 'react';
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import type { ControlRendererProps } from '../types';

const GaugeControl = memo<ControlRendererProps>(({ control, value, disabled, mode }) => {
  const gaugeType = control.type as 'circular-gauge' | 'linear-gauge' | 'thermometer' | 'progress-ring';
  const min = (control.config?.min as number) || 0;
  const max = (control.config?.max as number) || 100;
  const unit = (control.config?.unit as string) || '';
  const thresholds = (control.config?.thresholds as Array<{ value: number; color: string }>) || [];

  const numValue = typeof value === 'number' ? value : typeof value === 'string' ? parseFloat(value) : min;
  const clampedValue = Math.min(Math.max(numValue, min), max);
  const percentage = ((clampedValue - min) / (max - min)) * 100;

  // Get color based on thresholds
  const getThresholdColor = useMemo(() => {
    let color = '#8884d8'; // default blue
    for (const threshold of thresholds) {
      if (clampedValue <= threshold.value) {
        color = threshold.color;
        break;
      }
    }
    return color;
  }, [clampedValue, thresholds]);

  if (mode === 'view') {
    return (
      <div className="af-gauge-control__container af-gauge-control--view">
        <div className="af-gauge-control__value">
          {clampedValue}
          {unit && <span className="af-gauge-control__unit">{unit}</span>}
        </div>
      </div>
    );
  }

  // Circular Gauge (recharts RadialBarChart)
  if (gaugeType === 'circular-gauge' || gaugeType === 'progress-ring') {
    const data = [{ value: percentage }];
    const innerRadius = gaugeType === 'progress-ring' ? 60 : 50;
    const outerRadius = 80;

    return (
      <div className="af-gauge-control__container af-gauge-control--circular">
        {control.label && <div className="af-gauge-control__label">{control.label}</div>}

        <ResponsiveContainer width="100%" height={250}>
          <RadialBarChart
            cx="50%"
            cy="50%"
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            data={data}
            startAngle={90}
            endAngle={-270}
          >
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar
              background={{ fill: '#f0f0f0' }}
              dataKey="value"
              fill={getThresholdColor}
              cornerRadius={8}
              label={false}
            />
          </RadialBarChart>
        </ResponsiveContainer>

        <div className="af-gauge-control__reading">
          <span className="af-gauge-control__value">{clampedValue}</span>
          {unit && <span className="af-gauge-control__unit">{unit}</span>}
          <span className="af-gauge-control__range">
            {min} — {max}
          </span>
        </div>
      </div>
    );
  }

  // Linear Gauge (SVG horizontal bar)
  if (gaugeType === 'linear-gauge') {
    return (
      <div className="af-gauge-control__container af-gauge-control--linear">
        {control.label && <div className="af-gauge-control__label">{control.label}</div>}

        <div className="af-gauge-control__bar-wrapper">
          <div className="af-gauge-control__bar-background">
            <div
              className="af-gauge-control__bar-fill"
              style={{
                width: `${percentage}%`,
                backgroundColor: getThresholdColor,
              }}
            />
          </div>
          <div className="af-gauge-control__bar-label">
            <span className="af-gauge-control__value">{clampedValue}</span>
            {unit && <span className="af-gauge-control__unit">{unit}</span>}
          </div>
        </div>

        <div className="af-gauge-control__range-labels">
          <span>{min}</span>
          <span>{max}</span>
        </div>
      </div>
    );
  }

  // Thermometer Gauge (SVG vertical bar)
  if (gaugeType === 'thermometer') {
    return (
      <div className="af-gauge-control__container af-gauge-control--thermometer">
        {control.label && <div className="af-gauge-control__label">{control.label}</div>}

        <div className="af-gauge-control__thermometer-wrapper">
          <div className="af-gauge-control__thermometer-background">
            <div
              className="af-gauge-control__thermometer-fill"
              style={{
                height: `${percentage}%`,
                backgroundColor: getThresholdColor,
              }}
            />
          </div>
          <div className="af-gauge-control__thermometer-label">
            <span className="af-gauge-control__value">{clampedValue}</span>
            {unit && <span className="af-gauge-control__unit">{unit}</span>}
          </div>
        </div>

        <div className="af-gauge-control__range-labels af-gauge-control--vertical">
          <span>{max}</span>
          <span>{min}</span>
        </div>
      </div>
    );
  }

  return null;
});

GaugeControl.displayName = 'GaugeControl';

export default GaugeControl;
